<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { apiGet, apiSend } from "../api";
import { useToast } from "../stores/toast";
import Pager from "../components/Pager.vue";
import SortTh from "../components/SortTh.vue";

interface Product {
  id: string; sku: string; name: string; category: string;
  price: { amount: number }; status: string; stockTotal: number; stockState: string;
}
interface Category { slug: string; name: string }

const router = useRouter();
const toast = useToast();
const items = ref<Product[]>([]);
const cats = ref<Category[]>([]);
const loading = ref(true);
const error = ref("");
const busy = ref(false);

const fCat = ref("alle");
const fStatus = ref("alle");
const fStock = ref("alle");
const q = ref("");
const selected = ref<Set<string>>(new Set());

const limit = 50;
const offset = ref(0);
const total = ref(0);
const sort = ref("created");
const order = ref<"asc" | "desc">("desc");

const euro = (c: number) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);
const stockLabel: Record<string, string> = { in_stock: "op voorraad", low: "bijna op", out_of_stock: "uitverkocht" };
const hasFilters = () => fCat.value !== "alle" || fStatus.value !== "alle" || fStock.value !== "alle" || q.value !== "";
function wisFilters() { fCat.value = "alle"; fStatus.value = "alle"; fStock.value = "alle"; q.value = ""; }

async function load() {
  loading.value = true;
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset.value), sort: sort.value, order: order.value });
  if (fCat.value !== "alle") qs.set("category", fCat.value);
  if (fStatus.value !== "alle") qs.set("status", fStatus.value);
  if (fStock.value !== "alle") qs.set("stock", fStock.value);
  if (q.value.trim()) qs.set("q", q.value.trim());
  try {
    const d = await apiGet<{ items: Product[]; total: number }>(`/products?${qs.toString()}`);
    items.value = d.items; total.value = d.total;
    selected.value = new Set(); // selectie hoort bij de geladen pagina
  } catch (e) { error.value = String(e); } finally { loading.value = false; }
}
// Naar pagina 1 en herladen (zonder dubbele load).
function reload() { if (offset.value !== 0) offset.value = 0; else load(); }
function setSort(col: string) {
  if (sort.value === col) order.value = order.value === "asc" ? "desc" : "asc";
  else { sort.value = col; order.value = "asc"; }
}
watch([fCat, fStatus, fStock, sort, order], reload);
let qTimer: ReturnType<typeof setTimeout> | undefined;
watch(q, () => { clearTimeout(qTimer); qTimer = setTimeout(reload, 300); });
watch(offset, load);

function toggle(id: string) {
  const s = new Set(selected.value);
  s.has(id) ? s.delete(id) : s.add(id);
  selected.value = s;
}
function toggleAll() {
  if (items.value.every((p) => selected.value.has(p.id))) selected.value = new Set();
  else selected.value = new Set(items.value.map((p) => p.id));
}
function exportCsv() {
  const rows = items.value.filter((p) => selected.value.has(p.id));
  const head = "sku,naam,categorie,prijs_eur,voorraad,status";
  const body = rows.map((p) => [p.sku, `"${p.name}"`, p.category, (p.price.amount / 100).toFixed(2), p.stockTotal, p.status].join(","));
  const blob = new Blob([[head, ...body].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "binker-producten.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}
async function deleteSelected() {
  const ids = Array.from(selected.value);
  if (!ids.length || !confirm(`${ids.length} product(en) verwijderen?`)) return;
  busy.value = true;
  try {
    for (const id of ids) await apiSend("DELETE", `/products/${id}`);
    toast.show(`${ids.length} product(en) verwijderd`);
    await load();
  } catch (e) { error.value = `Verwijderen mislukt: ${(e as Error).message}`; } finally { busy.value = false; }
}

onMounted(async () => {
  try { cats.value = (await apiGet<{ items: Category[] }>("/categories")).items; } catch { /* chips optioneel */ }
  await load();
});
</script>

<template>
  <div>
    <div class="phead">
      <h1 class="title">Producten <span class="count">· {{ total }}</span></h1>
      <RouterLink to="/producten/nieuw" class="newbtn">+ Nieuw product</RouterLink>
    </div>

    <div class="filters">
      <div class="frow">
        <button class="chip" :class="{ on: fCat === 'alle' }" @click="fCat = 'alle'">Alle</button>
        <button v-for="c in cats" :key="c.slug" class="chip" :class="{ on: fCat === c.slug }" @click="fCat = c.slug">{{ c.name }}</button>
      </div>
      <div class="frow">
        <input v-model="q" class="search" placeholder="Zoek op naam of SKU…" />
        <span class="flbl">Status</span>
        <button v-for="s in ['alle','live','concept']" :key="s" class="chip sm" :class="{ on: fStatus === s }" @click="fStatus = s">{{ s === 'alle' ? 'Alle' : s }}</button>
        <span class="flbl">Voorraad</span>
        <button v-for="s in ['alle','in_stock','low','out_of_stock']" :key="s" class="chip sm" :class="{ on: fStock === s }" @click="fStock = s">{{ s === 'alle' ? 'Alle' : stockLabel[s] }}</button>
        <button v-if="hasFilters()" class="wis" @click="wisFilters">Wis filters</button>
        <span class="rescount">{{ total }} resultaten</span>
      </div>
    </div>

    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <template v-else>
      <table class="tbl">
        <thead>
          <tr>
            <th class="cb"><input type="checkbox" :checked="items.length > 0 && items.every((p) => selected.has(p.id))" @change="toggleAll" /></th>
            <SortTh label="Product" col="name" :sort="sort" :order="order" @sort="setSort" />
            <SortTh label="Categorie" col="category" :sort="sort" :order="order" @sort="setSort" />
            <SortTh label="Prijs" col="price" :sort="sort" :order="order" @sort="setSort" />
            <SortTh label="Voorraad" col="stock" :sort="sort" :order="order" @sort="setSort" />
            <SortTh label="Status" col="status" :sort="sort" :order="order" @sort="setSort" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in items" :key="p.id" class="clickrow" @click="router.push(`/producten/${p.id}/bewerken`)">
            <td class="cb" @click.stop><input type="checkbox" :checked="selected.has(p.id)" @change="toggle(p.id)" /></td>
            <td><div class="pname">{{ p.name }}</div><div class="psku">{{ p.sku }}</div></td>
            <td>{{ p.category }}</td>
            <td>{{ euro(p.price.amount) }}</td>
            <td :class="['stock', p.stockState]">{{ p.stockTotal }} — {{ stockLabel[p.stockState] }}</td>
            <td><span class="badge" :class="p.status">{{ p.status }}</span></td>
          </tr>
          <tr v-if="!items.length"><td colspan="6" class="empty">Geen producten gevonden.</td></tr>
        </tbody>
      </table>
      <Pager v-model:offset="offset" :limit="limit" :total="total" />
    </template>

    <Transition name="bulk">
      <div v-if="selected.size" class="bulkbar">
        <span>{{ selected.size }} geselecteerd</span>
        <button @click="exportCsv">Exporteren</button>
        <button class="danger" :disabled="busy" @click="deleteSelected">Verwijderen</button>
        <button class="x" @click="selected = new Set()">×</button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.phead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0; }
.count { color: var(--muted); font-weight: 600; }
.newbtn { background: var(--coral); color: #fff; text-decoration: none; border-radius: 999px; padding: 9px 16px; font-weight: 700; font-size: 14px; }
.filters { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.frow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.search { padding: 7px 12px; border: 1px solid var(--border-input); border-radius: 999px; font-size: 13px; min-width: 220px; }
.flbl { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted); margin-left: 6px; }
.chip { border: 1px solid var(--border-input); background: var(--surface); border-radius: 999px; padding: 6px 13px; font-size: 13px; font-weight: 600; cursor: pointer; text-transform: capitalize; }
.chip.on { background: var(--ink); color: #fff; border-color: var(--ink); }
.chip.sm { padding: 5px 11px; font-size: 12px; }
.chip.sm.on { background: var(--coral); border-color: var(--coral); }
.wis { border: 0; background: none; color: var(--coral); font-size: 13px; cursor: pointer; }
.rescount { margin-left: auto; color: var(--muted); font-size: 13px; }
.muted { color: var(--muted); } .err { color: var(--red); }
.tbl { width: 100%; border-collapse: collapse; font-size: 14px; }
.tbl th { text-align: left; color: var(--muted); font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 12px; border-bottom: 1px solid var(--hairline); }
.tbl td { padding: 14px 12px; border-bottom: 1px solid var(--hairline); }
.cb { width: 36px; }
.empty { text-align: center; color: var(--muted); padding: 28px 12px; }
.clickrow { cursor: pointer; } .clickrow:hover { background: var(--surface-off); }
.pname { font-weight: 600; } .psku { color: var(--muted); font-size: 12px; font-family: monospace; }
.stock.low { color: var(--amber); } .stock.out_of_stock { color: var(--red); } .stock.in_stock { color: var(--green); }
.badge { font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: 999px; }
.badge.live { color: var(--green); background: var(--green-bg); }
.badge.concept { color: var(--amber); background: var(--amber-bg); }
.bulkbar {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 90;
  display: flex; align-items: center; gap: 14px; background: var(--ink); color: #fff;
  padding: 12px 18px; border-radius: 999px; box-shadow: 0 12px 30px -8px rgba(20,20,20,.5); font-size: 14px;
}
.bulkbar button { border: 0; background: rgba(255,255,255,.15); color: #fff; border-radius: 999px; padding: 7px 14px; font-weight: 600; cursor: pointer; }
.bulkbar button.danger { background: var(--red); }
.bulkbar button.x { background: none; padding: 0 4px; font-size: 18px; }
.bulk-enter-active, .bulk-leave-active { transition: opacity .2s, transform .2s; }
.bulk-enter-from, .bulk-leave-to { opacity: 0; transform: translate(-50%, 10px); }
</style>
